import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AgentService, AuthService } from '@services';
import { ArchiveTimeoutOption } from '@interfaces';
import {
  DEFAULT_TIMEOUT_HOURS,
  ERROR_MESSAGE_DURATION_MS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGE_DURATION_MS,
  SUCCESS_MESSAGES,
} from './automations-dialog.constants';
import { MessageType } from './automations-dialog.types';

@Component({
  selector: 'unicon-automations-dialog',
  templateUrl: './automations-dialog.component.html',
  styleUrl: './automations-dialog.component.scss',
})
export class AutomationsDialogComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public isLoadingOptions = true;
  public isSaving = false;
  public saveMessage = '';
  public saveMessageType: MessageType = '';
  public archiveTimeoutOptions: ArchiveTimeoutOption[] = [];
  public selectedTimeoutId = DEFAULT_TIMEOUT_HOURS;

  private messageTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public dialogRef: MatDialogRef<AutomationsDialogComponent>,
    private agentService: AgentService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.loadArchiveTimeoutOptions();
    this.loadCurrentSettings();
  }

  ngOnDestroy(): void {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }

  private loadArchiveTimeoutOptions(): void {
    this.isLoadingOptions = true;
    this.agentService.getArchiveTimeoutOptions().subscribe({
      next: (options: ArchiveTimeoutOption[]) => {
        this.archiveTimeoutOptions = options;
        this.isLoadingOptions = false;
      },
      error: error => {
        console.error('Error loading archive timeout options:', error);
        this.isLoadingOptions = false;
        this.showErrorMessage(ERROR_MESSAGES.LOADING_OPTIONS);
      },
    });
  }

  private loadCurrentSettings(): void {
    const agentData = this.authService.getSessionValues();
    if (agentData?.leadArchiveTimeoutHours) {
      this.selectedTimeoutId = agentData.leadArchiveTimeoutHours;
    }
  }

  protected onTimeoutClick(event: Event, timeoutId: number): void {
    if (this.isSaving) {
      event.preventDefault();
      return;
    }

    if (this.selectedTimeoutId === timeoutId) {
      event.preventDefault();
      this.showErrorMessage(ERROR_MESSAGES.MUST_SELECT_OPTION);
      return;
    }

    this.onTimeoutChange(timeoutId);
  }

  private onTimeoutChange(timeoutId: number): void {
    const previousValue = this.selectedTimeoutId;
    this.selectedTimeoutId = timeoutId;
    this.isSaving = true;
    this.saveMessage = '';
    this.saveMessageType = '';

    this.agentService
      .updateArchiveSettings({ leadArchiveTimeoutHours: timeoutId })
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.updateAgentDataInStorage(timeoutId);
          this.showSuccessMessage(SUCCESS_MESSAGES.CONFIGURATION_UPDATED);
        },
        error: error => {
          console.error('Error updating archive settings:', error);
          this.selectedTimeoutId = previousValue;
          this.isSaving = false;
          this.showErrorMessage(ERROR_MESSAGES.UPDATE_ERROR);
        },
      });
  }

  private updateAgentDataInStorage(timeoutHours: number): void {
    const agentData = this.authService.getSessionValues();
    if (agentData) {
      agentData.leadArchiveTimeoutHours = timeoutHours;
      this.authService.setSessionValues(agentData);
    }
  }

  private showSuccessMessage(message: string): void {
    this.clearMessageTimeout();
    this.saveMessage = message;
    this.saveMessageType = 'success';

    this.messageTimeout = setTimeout(() => {
      this.saveMessage = '';
      this.saveMessageType = '';
    }, SUCCESS_MESSAGE_DURATION_MS);
  }

  private showErrorMessage(message: string): void {
    this.clearMessageTimeout();
    this.saveMessage = message;
    this.saveMessageType = 'error';

    this.messageTimeout = setTimeout(() => {
      this.saveMessage = '';
      this.saveMessageType = '';
    }, ERROR_MESSAGE_DURATION_MS);
  }

  private clearMessageTimeout(): void {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }
  }

  protected closeModal(): void {
    this.dialogRef.close();
  }

  protected isTimeoutSelected(timeoutId: number): boolean {
    return this.selectedTimeoutId === timeoutId;
  }
}
